import { browser, dev } from '$app/environment';
import { invalidate } from '$app/navigation';
import { BITSKI_API_V1 } from '$lib/constants';
import type {
  ActivityStatistics,
  CardOnFile,
  Payment,
  PaymentAction,
  Product,
} from '$lib/interfaces';
import { onMount } from 'svelte';
import * as uuid from 'uuid';
import { captureException } from '@sentry/browser';
import { expect } from './helpers';
import type { User } from '@bitskico/oauth-proxy-hooks/types';
import { z } from 'zod';

class ResponseError extends Error {
  constructor(
    message: string,
    public code: number,
  ) {
    super(message);
  }
}

export function getResponseErrorCode(e: unknown): number | undefined {
  return (e as ResponseError).code;
}

export const apiErrDecoder = z.object({
  error: z.object({
    message: z.string(),
  }),
});

let routeUrls: string[] | null = null;

export interface FetchJsonOptions {
  method?: string;
  credentials?: 'include';
  body?: unknown;
  skipErrorLog?: boolean;
  headers?: Record<string, string>;
}

/**
 * Helper for fetching JSON APIs. Throws an error with the
 *
 * @param fetch
 * @param url
 * @param init
 * @returns
 */
export async function fetchJson<T = any>(
  fetch: typeof window.fetch,
  url: string,
  { method = 'GET', body, credentials, skipErrorLog, headers = {} }: FetchJsonOptions = {},
): Promise<T> {
  try {
    routeUrls?.push(url);

    const response = await fetch(url, {
      method,
      credentials,
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (response.ok) {
      return response.json();
    } else {
      // If it's a client error and not 404, we should throw an error
      if (response.headers?.get('content-type') === 'application/json') {
        const responseJson = await response.json();

        const result = apiErrDecoder.safeParse(responseJson);

        if (result.success) {
          throw new ResponseError(result.data.error.message, response.status);
        }
      }

      throw new ResponseError('Sorry, something went wrong', response.status);
    }
  } catch (err) {
    if (!dev && !skipErrorLog) {
      captureException(err);
    }
    throw err;
  }
}

export async function withDependencies<T extends Record<string, unknown>>(
  load: () => Promise<T>,
): Promise<T & { dependencies: string[] }> {
  routeUrls = [];

  // TODO: Find a better way to handle this now that LoadOutput is no longer
  // publicly accessible (or maybe just wait until GraphQL)
  try {
    const props = await load();

    return {
      ...props,
      dependencies: routeUrls,
    };
  } finally {
    routeUrls = null;
  }
}

/**
 * Sets up intelligent route reloading. This will invalidate all URLs used
 * within the current route, either because the values weren't loaded in the
 * first place or because they should be polled.
 *
 * @param options
 */
export function setupReload(
  getDeps: () => string[],
  {
    shouldReloadWithBackoff,
    backoffStartInterval = 300,
    shouldPoll,
    pollingInterval = 10000,
  }: {
    shouldReloadWithBackoff?: () => boolean;
    backoffStartInterval?: number;
    shouldPoll?: () => boolean;
    pollingInterval?: number;
  },
): void {
  if (!browser) return;

  let isDestroyed = false;

  async function reload(timeout = backoffStartInterval) {
    try {
      await new Promise((resolve) => setTimeout(resolve, timeout));
      if (isDestroyed) return;

      await Promise.all(getDeps().map(invalidate));
      if (isDestroyed) return;

      if (shouldReloadWithBackoff?.()) {
        reload(timeout * 2);
      } else if (shouldPoll?.()) {
        reload(pollingInterval);
      }
    } catch {
      reload(timeout * 2);
    }
  }

  onMount(() => {
    if (shouldReloadWithBackoff?.()) {
      reload();
    } else if (shouldPoll?.() && !dev) {
      reload(pollingInterval);
    }

    return () => (isDestroyed = true);
  });
}

export async function saveCardOnFile(
  fetch: typeof window.fetch,
  user: User,
  token: string,
  isTestMode: boolean,
): Promise<CardOnFile> {
  try {
    const json = await fetchJson(fetch, `${BITSKI_API_V1}/users/${user.id}/payment-methods`, {
      method: 'POST',
      body: {
        paymentMethod: { token, testMode: isTestMode },
      },
    });

    if (json.paymentMethod) {
      return {
        paymentMethod: json.paymentMethod,
      };
    } else {
      throw new Error(json.error?.message || 'Something went wrong saving your card. Try again.');
    }
  } catch (err) {
    console.error(err);
    throw new Error('Something went wrong saving your card. Try again.');
  }
}

export async function loadCardOnFile(
  fetch: typeof window.fetch,
  user: User,
  isTestMode: boolean,
  delay = 300,
): Promise<CardOnFile | null> {
  try {
    const json = await fetchJson(
      fetch,
      `${BITSKI_API_V1}/users/${user.id}/payment-methods?test_mode=${isTestMode}`,
    );

    if ((json.paymentMethods || [])[0]) {
      return {
        paymentMethod: json.paymentMethods[0],
      };
    } else {
      return null;
    }
  } catch (error) {
    console.error(error);
    await new Promise((resolve) => setTimeout(resolve, delay));
    return loadCardOnFile(fetch, user, isTestMode, delay * 2);
  }
}

export async function createPendingPayment(
  fetch: typeof window.fetch,
  product: Product,
  cardOnFile: CardOnFile,
  currency: string,
  initialOwner: string,
  user: User,
): Promise<Payment> {
  const price = expect(
    product.prices.find((price) => price.currency === currency),
    'could not find a price with the correct currency',
  );

  const { payment } = await fetchJson(fetch, `${BITSKI_API_V1}/payments`, {
    method: 'POST',
    credentials: 'include',
    body: {
      payment: {
        id: uuid.v4(),
        currency: currency,
        productId: product.id,
        paymentMethodId: cardOnFile.paymentMethod.id,
        subtotal: price.price,
        fees: '0',
        taxes: '0',
        total: price.price,
        tokens: [],
        customerEmail: user.email,
      },
      initialOwner,
      autocomplete: false,
    },
  });

  return payment;
}

export const submitPayment = async (
  fetch: typeof window.fetch,
  payment: Payment,
  cardOnFile: CardOnFile,
  initialOwner: string,
  doublePaymentCheck = true,
): Promise<Payment | PaymentAction> => {
  const paymentRequest = {
    payment: {
      id: payment.id,
      status: 'completed',
      paymentMethodId: cardOnFile.paymentMethod.id,
      returnUrl: window.location.href,
    },
    initialOwner,
    doublePaymentCheck,
  };

  const { payment: updatedPayment } = await fetchJson<{ payment: Payment | PaymentAction }>(
    fetch,
    `${BITSKI_API_V1}/payments/${payment.id}`,
    {
      method: 'PATCH',
      body: paymentRequest,
    },
  );

  return updatedPayment;
};

export async function getAccounts(fetch: typeof window.fetch): Promise<string[]> {
  const json = await fetchJson(fetch, '/api/v1/web3/mainnet', {
    method: 'POST',
    body: { jsonrpc: '2.0', method: 'eth_accounts', params: [], id: 1 },
  });

  return json.result.map(z.string().parse);
}

export async function getFirstAccount(fetch: typeof window.fetch, user: User): Promise<string> {
  let account = user.accounts?.[0];

  if (!account) {
    account = (await getAccounts(fetch))[0]!;
  }

  return account;
}

export async function getActivityStatisticsForWallet(
  fetch: typeof window.fetch,
  address: string,
): Promise<ActivityStatistics> {
  try {
    const res = await fetch(`https://api.bitski.com/v2/activity-statistics?address=${address}`);
    if (!res.ok) {
      throw new Error('Something went wrong! Try again');
    }

    const { statistics } = (await res.json()) as { statistics: ActivityStatistics };

    return statistics;
  } catch (error) {
    console.warn('error', error);
    throw error;
  }
}
